import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Box,
  Text,
  SimpleGrid,
  Image,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { FaUpload, FaTrashAlt, FaFolderOpen } from "react-icons/fa";
import DatePicker from "../DateTimePicker";
import axios from "axios";
import JSZip from "jszip";
import constants from "../../util/constant";
import { useAuth } from "../../hooks/AuthProvider";

const IMAGE_VIEWS = [
  { id: "front", label: "ภาพถ่ายฟันหน้า" },
  { id: "left", label: "ภาพถ่ายฟันด้านซ้าย" },
  { id: "right", label: "ภาพถ่ายฟันด้านขวา" },
  { id: "upper", label: "ภาพถ่ายฟันด้านบน" },
  { id: "lower", label: "ภาพถ่ายฟันด้านล่าง" },
  { id: "CC1", label: "CC1 — Chief complaint 1" },
  { id: "CC2", label: "CC2 — Chief complaint 2" },
  { id: "CC3", label: "CC3 — Chief complaint 3" },
  { id: "CC4", label: "CC4 — Chief complaint 4" },
  { id: "CC5", label: "CC5 — Chief complaint 5" },
];

const ALLOWED_IMAGE_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_ZIP_SIZE = 50 * 1024 * 1024;
const MAX_EXTRACTED_SIZE = 50 * 1024 * 1024;

const getExtension = (fileName) => fileName.split(".").pop()?.toLowerCase();

// Match imported filenames case-insensitively, keeping canonical API values.
const getViewFromFileName = (fileName) => {
  const name = fileName.replace(/\.[^.]+$/, "").toLowerCase();
  return IMAGE_VIEWS.find(({ id }) => id.toLowerCase() === name)?.id;
};

const canDecodeImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    bitmap.close();
    return;
  }

  await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("ไม่สามารถอ่านไฟล์ภาพได้"));
    };
    image.src = url;
  });
};

const validateImage = async (file) => {
  const extension = getExtension(file.name);
  if (!ALLOWED_IMAGE_TYPES[extension]) {
    throw new Error(`ไฟล์ ${file.name} ต้องเป็น JPG, PNG หรือ WEBP`);
  }
  if (file.size === 0 || file.size > MAX_IMAGE_SIZE) {
    throw new Error(`ไฟล์ ${file.name} ต้องมีขนาดไม่เกิน 10 MB`);
  }
  await canDecodeImage(file);
};

const ImageUploadModal = ({ isOpen, onClose, patientId, onRefresh }) => {
  const [images, setImages] = useState(() =>
    Object.fromEntries(
      IMAGE_VIEWS.map(({ id }) => [
        id,
        { file: null, imageId: null, fileName: null },
      ]),
    ),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReadingZip, setIsReadingZip] = useState(false);
  const [isReadingDirectory, setIsReadingDirectory] = useState(false);
  const [zipName, setZipName] = useState("");
  const [directoryName, setDirectoryName] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const auth = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (!date) {
      const now = new Date();
      const defaultDate = now.toISOString().slice(0, 16);
      setDate(defaultDate);
    }
  }, [date]);

  const handleImageChange = async (view, file) => {
    if (!file) return;
    try {
      await validateImage(file);
    } catch (error) {
      toast({
        title: "ไฟล์ภาพไม่ถูกต้อง",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setZipName("");
    setDirectoryName("");
    setImages((prev) => ({
      ...prev,
      [view]: { ...prev[view], file },
    }));
  };

  const handleZipChange = async (zipFile) => {
    if (!zipFile) return;
    if (!zipFile.name.toLowerCase().endsWith(".zip")) {
      toast({
        title: "กรุณาเลือกไฟล์ ZIP",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (zipFile.size === 0 || zipFile.size > MAX_ZIP_SIZE) {
      toast({
        title: "ไฟล์ ZIP ไม่ถูกต้อง",
        description: "ไฟล์ ZIP ต้องมีขนาดไม่เกิน 50 MB",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsReadingZip(true);
    try {
      const zip = await JSZip.loadAsync(zipFile, { checkCRC32: true });
      const entries = Object.values(zip.files).filter(
        (entry) =>
          !entry.dir &&
          !entry.name.startsWith("__MACOSX/") &&
          !entry.name.split("/").pop().startsWith("."),
      );
      const filesByView = {};
      let declaredSize = 0;
      let extractedSize = 0;

      for (const entry of entries) {
        const baseName = entry.name.split("/").pop();
        const extension = getExtension(baseName);
        const view = getViewFromFileName(baseName);

        if (
          !ALLOWED_IMAGE_TYPES[extension] ||
          !IMAGE_VIEWS.some(({ id }) => id === view)
        ) {
          throw new Error(`ชื่อไฟล์ไม่ถูกต้อง: ${entry.name}`);
        }
        if (filesByView[view]) {
          throw new Error(`พบภาพ ${view} ซ้ำในไฟล์ ZIP`);
        }

        declaredSize += entry._data?.uncompressedSize || 0;
        if (declaredSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดไฟล์หลังแตก ZIP ต้องไม่เกิน 50 MB");
        }

        const blob = await entry.async("blob");
        extractedSize += blob.size;
        if (extractedSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดไฟล์หลังแตก ZIP ต้องไม่เกิน 50 MB");
        }
        const imageFile = new File([blob], baseName, {
          type: ALLOWED_IMAGE_TYPES[extension],
        });
        await validateImage(imageFile);
        filesByView[view] = imageFile;
      }

      const importedViewCount = Object.keys(filesByView).length;
      if (importedViewCount === 0) {
        throw new Error("ไฟล์ ZIP ต้องมีภาพที่รองรับอย่างน้อย 1 ภาพ");
      }

      setImages((prev) => {
        const next = { ...prev };
        IMAGE_VIEWS.forEach(({ id }) => {
          next[id] = {
            file: filesByView[id] || null,
            imageId: null,
            fileName: filesByView[id]?.name || null,
          };
        });
        return next;
      });
      setZipName(zipFile.name);
      setDirectoryName("");
      toast({
        title: "ตรวจสอบไฟล์ ZIP สำเร็จ",
        description: `พบภาพที่ถูกต้อง ${importedViewCount} ภาพ`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Invalid ZIP file:", error);
      toast({
        title: "ไฟล์ ZIP ไม่ถูกต้อง",
        description: error.message || "ไม่สามารถอ่านไฟล์ ZIP ได้",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsReadingZip(false);
    }
  };

  const handleDirectoryChange = async (fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length) return;

    setIsReadingDirectory(true);
    try {
      const visibleFiles = selectedFiles.filter((file) => {
        const parts = (file.webkitRelativePath || file.name).split("/");
        return !parts.some((part) => part.startsWith("."));
      });

      if (visibleFiles.length === 0) {
        throw new Error("โฟลเดอร์ต้องมีภาพที่รองรับอย่างน้อย 1 ภาพ");
      }
      if (visibleFiles.length > IMAGE_VIEWS.length) {
        throw new Error(`โฟลเดอร์มีไฟล์มากกว่า ${IMAGE_VIEWS.length} ภาพที่กำหนด`);
      }

      const filesByView = {};
      let totalSize = 0;

      for (const file of visibleFiles) {
        const relativeParts = (file.webkitRelativePath || file.name).split("/");
        if (relativeParts.length > 2) {
          throw new Error(`ไม่รองรับโฟลเดอร์ย่อย: ${file.webkitRelativePath}`);
        }

        const extension = getExtension(file.name);
        const view = getViewFromFileName(file.name);
        if (
          !ALLOWED_IMAGE_TYPES[extension] ||
          !IMAGE_VIEWS.some(({ id }) => id === view)
        ) {
          throw new Error(`ชื่อไฟล์ไม่ถูกต้อง: ${file.name}`);
        }
        if (filesByView[view]) {
          throw new Error(`พบภาพ ${view} ซ้ำในโฟลเดอร์`);
        }

        totalSize += file.size;
        if (totalSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดภาพทั้งหมดในโฟลเดอร์ต้องไม่เกิน 50 MB");
        }

        await validateImage(file);
        filesByView[view] = file;
      }

      const importedViewCount = Object.keys(filesByView).length;

      setImages((prev) => {
        const next = { ...prev };
        IMAGE_VIEWS.forEach(({ id }) => {
          next[id] = {
            file: filesByView[id] || null,
            imageId: null,
            fileName: filesByView[id]?.name || null,
          };
        });
        return next;
      });

      setDirectoryName(
        visibleFiles[0].webkitRelativePath?.split("/")[0] || "โฟลเดอร์ที่เลือก",
      );
      setZipName("");
      toast({
        title: "ตรวจสอบโฟลเดอร์สำเร็จ",
        description: `พบภาพที่ถูกต้อง ${importedViewCount} ภาพ`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Invalid image directory:", error);
      toast({
        title: "โฟลเดอร์ไม่ถูกต้อง",
        description: error.message || "ไม่สามารถอ่านโฟลเดอร์ได้",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsReadingDirectory(false);
    }
  };

  const handleImageDelete = (view) => {
    setZipName("");
    setDirectoryName("");
    setImages((prev) => ({
      ...prev,
      [view]: { file: null, imageId: null, fileName: null },
    }));
  };

  const callDetectionAPI = async (fileName, imageId) => {
    console.log(`fileName: ${fileName}`);
    console.log(`imageId: ${imageId}`);

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `https://flaskapi.dent.lingosoft.co/test_detect?img=${fileName}&image_id=${imageId}&socket_id=null`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
      })
      .catch((error) => {
        console.error("Detection API error:", error);
      });
  };

  const uploadImageToDiagnosis = async (file, viewType, diagnosisId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("view_type", viewType);
    formData.append("diagnosis_id", diagnosisId);

    try {
      const response = await axios.post(
        `${constants.host}/upload_img`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // Call detection API after successful upload
      await callDetectionAPI(response.data.file, response.data.image_id);

      return response.data;
    } catch (error) {
      console.error(`Failed to upload ${viewType} image:`, error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // 1. First, create the diagnosis record
      const submissionData = {
        patients_id: patientId,
        // Preserve the existing diagnosis API value; CC views are separate image records.
        number_image: "five",
        diagnosis_status: "waiting",
        note,
        inspector_id: auth.user.uid,
        created_datetime: date, // Include the selected date
      };

      //console.log(`user: ${JSON.stringify(auth.user)}`)
      console.log(submissionData);

      const diagnosisResponse = await axios.post(
        `${constants.mobile_backend_host}/diagnosis/insert_diagnosis`,
        submissionData,
      );

      const diagnosisId = diagnosisResponse.data.data.diagnosis_id;

      // 2. Then upload each image with the diagnosis_id
      const imageUploads = Object.entries(images)
        .filter(([_, img]) => img.file !== null)
        .map(async ([view, img]) => {
          try {
            await uploadImageToDiagnosis(img.file, view, diagnosisId);
          } catch (error) {
            console.error(`Failed to upload ${view} image:`, error);
            throw error;
          }
        });

      await Promise.all(imageUploads);

      toast({
        title: "Upload successful",
        description: "All images have been uploaded successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onRefresh();
      onClose();
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading the images",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnyImageSelected = Object.values(images).some(
    (img) => img.file !== null,
  );

  return (
    <Modal size={"3xl"} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>อัพโหลดภาพ</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={6}>
            <Text mb={2} fontWeight="bold">
              เลือกวันที่ถ่ายภาพ
            </Text>
            <DatePicker
              initialDate={date}
              onDateChange={(newDate) => setDate(newDate)}
            />
          </Box>
          <Box mb={6}>
            <Text mb={2} fontWeight="bold">
              หมายเหตุ
            </Text>
            <Input
              placeholder="ระบุหมายเหตุ (ถ้ามี)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
          <Box mb={6} p={4} borderWidth="1px" borderRadius="md">
            <Text mb={2} fontWeight="bold">
              อัพโหลดภาพด้วย ZIP หรือโฟลเดอร์
            </Text>
            <Text mb={3} fontSize="sm" color="gray.600">
              ชื่อภาพที่รองรับคือ front, left, right, upper, lower และ CC1–CC5
              (Chief complaint 1–5) เช่น CC1.jpg หรือ cc2.png
              ไม่แยกตัวพิมพ์เล็ก/ใหญ่ เลือกได้ 1–10 ภาพ ไม่จำเป็นต้องครบทุกภาพ
              และไม่ซ้ำประเภท รองรับ JPG, JPEG, PNG และ WEBP
              (ไม่เกิน 10 MB ต่อภาพ, ZIP และภาพรวมหลังแตกไม่เกิน 50 MB)
            </Text>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Button
                leftIcon={<FaUpload />}
                colorScheme="purple"
                variant="outline"
                as="label"
                cursor={isReadingZip ? "not-allowed" : "pointer"}
                isLoading={isReadingZip}
                isDisabled={isReadingZip || isReadingDirectory || isSubmitting}
              >
                เลือกไฟล์ ZIP
                <Input
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => {
                    handleZipChange(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                  hidden
                />
              </Button>
              <Button
                leftIcon={<FaFolderOpen />}
                colorScheme="teal"
                variant="outline"
                as="label"
                cursor={isReadingDirectory ? "not-allowed" : "pointer"}
                isLoading={isReadingDirectory}
                isDisabled={isReadingZip || isReadingDirectory || isSubmitting}
              >
                เลือกโฟลเดอร์
                <Input
                  type="file"
                  multiple
                  webkitdirectory=""
                  directory=""
                  onChange={(e) => {
                    handleDirectoryChange(e.target.files);
                    e.target.value = "";
                  }}
                  hidden
                />
              </Button>
            </Box>
            {zipName && (
              <Text mt={2} fontSize="sm" color="green.600">
                ไฟล์ที่ตรวจสอบแล้ว: {zipName}
              </Text>
            )}
            {directoryName && (
              <Text mt={2} fontSize="sm" color="green.600">
                โฟลเดอร์ที่ตรวจสอบแล้ว: {directoryName}
              </Text>
            )}
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {IMAGE_VIEWS.map(({ id, label }) => (
              <Box key={id} width="100%">
                <Text mb={2} fontWeight="bold">
                  {label}
                </Text>
                {images[id].file ? (
                  <Box position="relative">
                    <Image
                      src={URL.createObjectURL(images[id].file)}
                      alt={label}
                      borderRadius="8px"
                      objectFit="cover"
                      width="100%"
                      height="200px"
                    />
                    <IconButton
                      aria-label="Delete image"
                      icon={<FaTrashAlt />}
                      colorScheme="red"
                      size="sm"
                      position="absolute"
                      top="5px"
                      right="5px"
                      onClick={() => handleImageDelete(id)}
                    />
                  </Box>
                ) : (
                  <Button
                    leftIcon={<FaUpload />}
                    colorScheme="teal"
                    variant="outline"
                    as="label"
                    cursor="pointer"
                    width="100%"
                  >
                    เลือกไฟล์
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageChange(id, e.target.files?.[0])
                      }
                      hidden
                    />
                  </Button>
                )}
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSubmit}
            isDisabled={
              !date ||
              !isAnyImageSelected ||
              isSubmitting ||
              isReadingZip ||
              isReadingDirectory
            }
            isLoading={isSubmitting}
          >
            อัพโหลด
          </Button>
          <Button onClick={onClose} isDisabled={isSubmitting}>
            ยกเลิก
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageUploadModal;


/*
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Box,
  Text,
  SimpleGrid,
  Image,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { FaUpload, FaTrashAlt, FaFolderOpen } from "react-icons/fa";
import DatePicker from "../DateTimePicker";
import axios from "axios";
import JSZip from "jszip";
import constants from "../../util/constant";
import { useAuth } from "../../hooks/AuthProvider";

const IMAGE_VIEWS = [
  { id: "front", label: "ภาพถ่ายฟันหน้า" },
  { id: "left", label: "ภาพถ่ายฟันด้านซ้าย" },
  { id: "right", label: "ภาพถ่ายฟันด้านขวา" },
  { id: "upper", label: "ภาพถ่ายฟันด้านบน" },
  { id: "lower", label: "ภาพถ่ายฟันด้านล่าง" },
];

const ALLOWED_IMAGE_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_ZIP_SIZE = 50 * 1024 * 1024;
const MAX_EXTRACTED_SIZE = 50 * 1024 * 1024;

const getExtension = (fileName) => fileName.split(".").pop()?.toLowerCase();

const canDecodeImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    bitmap.close();
    return;
  }

  await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("ไม่สามารถอ่านไฟล์ภาพได้"));
    };
    image.src = url;
  });
};

const validateImage = async (file) => {
  const extension = getExtension(file.name);
  if (!ALLOWED_IMAGE_TYPES[extension]) {
    throw new Error(`ไฟล์ ${file.name} ต้องเป็น JPG, PNG หรือ WEBP`);
  }
  if (file.size === 0 || file.size > MAX_IMAGE_SIZE) {
    throw new Error(`ไฟล์ ${file.name} ต้องมีขนาดไม่เกิน 10 MB`);
  }
  await canDecodeImage(file);
};

const ImageUploadModal = ({ isOpen, onClose, patientId, onRefresh }) => {
  const [images, setImages] = useState({
    front: { file: null, imageId: null, fileName: null },
    left: { file: null, imageId: null, fileName: null },
    right: { file: null, imageId: null, fileName: null },
    upper: { file: null, imageId: null, fileName: null },
    lower: { file: null, imageId: null, fileName: null },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReadingZip, setIsReadingZip] = useState(false);
  const [isReadingDirectory, setIsReadingDirectory] = useState(false);
  const [zipName, setZipName] = useState("");
  const [directoryName, setDirectoryName] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const auth = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (!date) {
      const now = new Date();
      const defaultDate = now.toISOString().slice(0, 16);
      setDate(defaultDate);
    }
  }, [date]);

  const handleImageChange = async (view, file) => {
    if (!file) return;
    try {
      await validateImage(file);
    } catch (error) {
      toast({
        title: "ไฟล์ภาพไม่ถูกต้อง",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setZipName("");
    setDirectoryName("");
    setImages((prev) => ({
      ...prev,
      [view]: { ...prev[view], file },
    }));
  };

  const handleZipChange = async (zipFile) => {
    if (!zipFile) return;
    if (!zipFile.name.toLowerCase().endsWith(".zip")) {
      toast({
        title: "กรุณาเลือกไฟล์ ZIP",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (zipFile.size === 0 || zipFile.size > MAX_ZIP_SIZE) {
      toast({
        title: "ไฟล์ ZIP ไม่ถูกต้อง",
        description: "ไฟล์ ZIP ต้องมีขนาดไม่เกิน 50 MB",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsReadingZip(true);
    try {
      const zip = await JSZip.loadAsync(zipFile, { checkCRC32: true });
      const entries = Object.values(zip.files).filter(
        (entry) =>
          !entry.dir &&
          !entry.name.startsWith("__MACOSX/") &&
          !entry.name.split("/").pop().startsWith("."),
      );
      const filesByView = {};
      let declaredSize = 0;
      let extractedSize = 0;

      for (const entry of entries) {
        const baseName = entry.name.split("/").pop();
        const extension = getExtension(baseName);
        const view = baseName.replace(/\.[^.]+$/, "").toLowerCase();

        if (
          !ALLOWED_IMAGE_TYPES[extension] ||
          !IMAGE_VIEWS.some(({ id }) => id === view)
        ) {
          throw new Error(`ชื่อไฟล์ไม่ถูกต้อง: ${entry.name}`);
        }
        if (filesByView[view]) {
          throw new Error(`พบภาพ ${view} ซ้ำในไฟล์ ZIP`);
        }

        declaredSize += entry._data?.uncompressedSize || 0;
        if (declaredSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดไฟล์หลังแตก ZIP ต้องไม่เกิน 50 MB");
        }

        const blob = await entry.async("blob");
        extractedSize += blob.size;
        if (extractedSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดไฟล์หลังแตก ZIP ต้องไม่เกิน 50 MB");
        }
        const imageFile = new File([blob], baseName, {
          type: ALLOWED_IMAGE_TYPES[extension],
        });
        await validateImage(imageFile);
        filesByView[view] = imageFile;
      }

      const importedViewCount = Object.keys(filesByView).length;
      if (importedViewCount === 0) {
        throw new Error("ไฟล์ ZIP ต้องมีภาพที่รองรับอย่างน้อย 1 ภาพ");
      }

      setImages((prev) => {
        const next = { ...prev };
        IMAGE_VIEWS.forEach(({ id }) => {
          next[id] = {
            file: filesByView[id] || null,
            imageId: null,
            fileName: filesByView[id]?.name || null,
          };
        });
        return next;
      });
      setZipName(zipFile.name);
      setDirectoryName("");
      toast({
        title: "ตรวจสอบไฟล์ ZIP สำเร็จ",
        description: `พบภาพที่ถูกต้อง ${importedViewCount} ภาพ`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Invalid ZIP file:", error);
      toast({
        title: "ไฟล์ ZIP ไม่ถูกต้อง",
        description: error.message || "ไม่สามารถอ่านไฟล์ ZIP ได้",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsReadingZip(false);
    }
  };

  const handleDirectoryChange = async (fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length) return;

    setIsReadingDirectory(true);
    try {
      const visibleFiles = selectedFiles.filter((file) => {
        const parts = (file.webkitRelativePath || file.name).split("/");
        return !parts.some((part) => part.startsWith("."));
      });

      if (visibleFiles.length === 0) {
        throw new Error("โฟลเดอร์ต้องมีภาพที่รองรับอย่างน้อย 1 ภาพ");
      }
      if (visibleFiles.length > IMAGE_VIEWS.length) {
        throw new Error("โฟลเดอร์มีไฟล์มากกว่า 5 ภาพที่กำหนด");
      }

      const filesByView = {};
      let totalSize = 0;

      for (const file of visibleFiles) {
        const relativeParts = (file.webkitRelativePath || file.name).split("/");
        if (relativeParts.length > 2) {
          throw new Error(`ไม่รองรับโฟลเดอร์ย่อย: ${file.webkitRelativePath}`);
        }

        const extension = getExtension(file.name);
        const view = file.name.replace(/\.[^.]+$/, "").toLowerCase();
        if (
          !ALLOWED_IMAGE_TYPES[extension] ||
          !IMAGE_VIEWS.some(({ id }) => id === view)
        ) {
          throw new Error(`ชื่อไฟล์ไม่ถูกต้อง: ${file.name}`);
        }
        if (filesByView[view]) {
          throw new Error(`พบภาพ ${view} ซ้ำในโฟลเดอร์`);
        }

        totalSize += file.size;
        if (totalSize > MAX_EXTRACTED_SIZE) {
          throw new Error("ขนาดภาพทั้งหมดในโฟลเดอร์ต้องไม่เกิน 50 MB");
        }

        await validateImage(file);
        filesByView[view] = file;
      }

      const importedViewCount = Object.keys(filesByView).length;

      setImages((prev) => {
        const next = { ...prev };
        IMAGE_VIEWS.forEach(({ id }) => {
          next[id] = {
            file: filesByView[id] || null,
            imageId: null,
            fileName: filesByView[id]?.name || null,
          };
        });
        return next;
      });

      setDirectoryName(
        visibleFiles[0].webkitRelativePath?.split("/")[0] || "โฟลเดอร์ที่เลือก",
      );
      setZipName("");
      toast({
        title: "ตรวจสอบโฟลเดอร์สำเร็จ",
        description: `พบภาพที่ถูกต้อง ${importedViewCount} ภาพ`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Invalid image directory:", error);
      toast({
        title: "โฟลเดอร์ไม่ถูกต้อง",
        description: error.message || "ไม่สามารถอ่านโฟลเดอร์ได้",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsReadingDirectory(false);
    }
  };

  const handleImageDelete = (view) => {
    setZipName("");
    setDirectoryName("");
    setImages((prev) => ({
      ...prev,
      [view]: { file: null, imageId: null, fileName: null },
    }));
  };

  const callDetectionAPI = async (fileName, imageId) => {
    console.log(`fileName: ${fileName}`);
    console.log(`imageId: ${imageId}`);

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `https://flaskapi.dent.lingosoft.co/test_detect?img=${fileName}&image_id=${imageId}&socket_id=null`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
      })
      .catch((error) => {
        console.error("Detection API error:", error);
      });
  };

  const uploadImageToDiagnosis = async (file, viewType, diagnosisId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("view_type", viewType);
    formData.append("diagnosis_id", diagnosisId);

    try {
      const response = await axios.post(
        `${constants.host}/upload_img`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // Call detection API after successful upload
      await callDetectionAPI(response.data.file, response.data.image_id);

      return response.data;
    } catch (error) {
      console.error(`Failed to upload ${viewType} image:`, error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // 1. First, create the diagnosis record
      const submissionData = {
        patients_id: patientId,
        number_image: "five",
        diagnosis_status: "waiting",
        note,
        inspector_id: auth.user.uid,
        created_datetime: date, // Include the selected date
      };

      //console.log(`user: ${JSON.stringify(auth.user)}`)
      console.log(submissionData);

      const diagnosisResponse = await axios.post(
        `${constants.mobile_backend_host}/diagnosis/insert_diagnosis`,
        submissionData,
      );

      const diagnosisId = diagnosisResponse.data.data.diagnosis_id;

      // 2. Then upload each image with the diagnosis_id
      const imageUploads = Object.entries(images)
        .filter(([_, img]) => img.file !== null)
        .map(async ([view, img]) => {
          try {
            await uploadImageToDiagnosis(img.file, view, diagnosisId);
          } catch (error) {
            console.error(`Failed to upload ${view} image:`, error);
            throw error;
          }
        });

      await Promise.all(imageUploads);

      toast({
        title: "Upload successful",
        description: "All images have been uploaded successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onRefresh();
      onClose();
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading the images",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnyImageSelected = Object.values(images).some(
    (img) => img.file !== null,
  );

  return (
    <Modal size={"3xl"} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>อัพโหลดภาพ</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={6}>
            <Text mb={2} fontWeight="bold">
              เลือกวันที่ถ่ายภาพ
            </Text>
            <DatePicker
              initialDate={date}
              onDateChange={(newDate) => setDate(newDate)}
            />
          </Box>
          <Box mb={6}>
            <Text mb={2} fontWeight="bold">
              หมายเหตุ
            </Text>
            <Input
              placeholder="ระบุหมายเหตุ (ถ้ามี)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
          <Box mb={6} p={4} borderWidth="1px" borderRadius="md">
            <Text mb={2} fontWeight="bold">
              อัพโหลดภาพด้วย ZIP หรือโฟลเดอร์
            </Text>
            <Text mb={3} fontSize="sm" color="gray.600">
              ชื่อภาพที่รองรับคือ front, left, right, upper และ lower
              โดยไม่จำเป็นต้องมีครบทุกภาพ รองรับ JPG, PNG และ WEBP (ไม่เกิน 10
              MB ต่อภาพ)
            </Text>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Button
                leftIcon={<FaUpload />}
                colorScheme="purple"
                variant="outline"
                as="label"
                cursor={isReadingZip ? "not-allowed" : "pointer"}
                isLoading={isReadingZip}
                isDisabled={isReadingZip || isReadingDirectory || isSubmitting}
              >
                เลือกไฟล์ ZIP
                <Input
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => {
                    handleZipChange(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                  hidden
                />
              </Button>
              <Button
                leftIcon={<FaFolderOpen />}
                colorScheme="teal"
                variant="outline"
                as="label"
                cursor={isReadingDirectory ? "not-allowed" : "pointer"}
                isLoading={isReadingDirectory}
                isDisabled={isReadingZip || isReadingDirectory || isSubmitting}
              >
                เลือกโฟลเดอร์
                <Input
                  type="file"
                  multiple
                  webkitdirectory=""
                  directory=""
                  onChange={(e) => {
                    handleDirectoryChange(e.target.files);
                    e.target.value = "";
                  }}
                  hidden
                />
              </Button>
            </Box>
            {zipName && (
              <Text mt={2} fontSize="sm" color="green.600">
                ไฟล์ที่ตรวจสอบแล้ว: {zipName}
              </Text>
            )}
            {directoryName && (
              <Text mt={2} fontSize="sm" color="green.600">
                โฟลเดอร์ที่ตรวจสอบแล้ว: {directoryName}
              </Text>
            )}
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {IMAGE_VIEWS.map(({ id, label }) => (
              <Box key={id} width="100%">
                <Text mb={2} fontWeight="bold">
                  {label}
                </Text>
                {images[id].file ? (
                  <Box position="relative">
                    <Image
                      src={URL.createObjectURL(images[id].file)}
                      alt={label}
                      borderRadius="8px"
                      objectFit="cover"
                      width="100%"
                      height="200px"
                    />
                    <IconButton
                      aria-label="Delete image"
                      icon={<FaTrashAlt />}
                      colorScheme="red"
                      size="sm"
                      position="absolute"
                      top="5px"
                      right="5px"
                      onClick={() => handleImageDelete(id)}
                    />
                  </Box>
                ) : (
                  <Button
                    leftIcon={<FaUpload />}
                    colorScheme="teal"
                    variant="outline"
                    as="label"
                    cursor="pointer"
                    width="100%"
                  >
                    เลือกไฟล์
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageChange(id, e.target.files?.[0])
                      }
                      hidden
                    />
                  </Button>
                )}
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSubmit}
            isDisabled={
              !date ||
              !isAnyImageSelected ||
              isSubmitting ||
              isReadingZip ||
              isReadingDirectory
            }
            isLoading={isSubmitting}
          >
            อัพโหลด
          </Button>
          <Button onClick={onClose} isDisabled={isSubmitting}>
            ยกเลิก
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageUploadModal;
*/